import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { FetchAllCurrencies } from 'src/app/features/customers/store/customers.actions';
import { filter, Observable, of, take } from 'rxjs';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { ICurrencyPrice, IVariant } from '../../../models/products.models';

interface AddedCurrencyControl {
  data: ICurrency;
  controls: {
    currency: FormControl; // FormControl for the currency select
    variants: FormGroup[]; // Array of form groups, one per variant
  };
  controlName: string; // Name of the control in the form
}

@Component({
  selector: 'app-edit-variant-product-pricing',
  templateUrl: './edit-variant-product-pricing.component.html',
  styleUrls: ['./edit-variant-product-pricing.component.scss']
})
export class EditVariantProductPricingComponent implements OnInit {

  form!: FormGroup;
  currencies$: Observable<ICurrency[]> = of([]);
  default_currency!: ICurrency | null | undefined;
  currencies: ICurrency[] = [];
  availableCurrencies: ICurrency[] = [];
  addedCurrencies: AddedCurrencyControl[] = [];
  variants: IVariant[] = [];
  variantsFormArray: FormGroup[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditVariantProductPricingComponent>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    // Initialize variants from data
    this.variants = this.data?.variants || [];
    
    // Initialize form with default currency first
    const defaultCurrency = this.data?.default_currency;
    this.form = this.fb.group({
      default_currency: [{ value: defaultCurrency || null, disabled: true }],
    });

    // Initialize form groups for each variant (default currency prices)
    this.variants.forEach((variant, index) => {
      const variantGroup = this.fb.group({
        variant_id: [variant.id],
        cost: [variant.cost ?? '', [Validators.required, Validators.min(0)]],
        sales_price: [variant.sales_price ?? '', [Validators.required, Validators.min(0)]],
      });
      this.variantsFormArray.push(variantGroup);
      this.form.addControl(`variant_${index}`, variantGroup);
    });
    
    // Then subscribe to currencies
    this.store.dispatch(FetchAllCurrencies());
    this.store.select((state) => state.customers.allCurrencies).pipe(
      filter((currencies) => Array.isArray(currencies) && currencies.length > 0),
      take(1)
    ).subscribe((currencies) => {
      this.handleCurrencies(currencies);
    });
  }

  private handleCurrencies(currencies: ICurrency[]): void {
    this.addedCurrencies = [];
    this.currencies = currencies.filter(currency => !currency.is_default);

    this.default_currency = currencies.find(currency => currency.is_default);
    
    // Update the form with the default currency
    if (this.default_currency && this.form) {
      this.form.patchValue({
        default_currency: this.default_currency
      });
    }

    // Find currencies that are used in any variant
    const usedCurrencyIds = new Set<number>();
    this.variants.forEach(variant => {
      variant.currency_prices?.forEach((price: ICurrencyPrice) => {
        if (price.currency_id && price.currency_id !== this.default_currency?.id) {
          usedCurrencyIds.add(price.currency_id);
        }
      });
    });

    // Initialize added currencies for currencies that are already used
    const currenciesToAdd = this.currencies.filter(c => usedCurrencyIds.has(c.id));
    currenciesToAdd.forEach(currency => {
      this.addCurrency(currency);
    });

    // Update available currencies
    this.recomputeAvailableCurrencies();
  }

  recomputeAvailableCurrencies(): void {
    const usedIds = new Set(this.addedCurrencies.map(c => c.data.id));
    this.availableCurrencies = this.currencies.filter(c => !usedIds.has(c.id));
  }

  getAvailableCurrenciesForSelect(index: number): ICurrency[] {
    const currentCurrency = this.addedCurrencies[index]?.data;
    const usedIds = new Set(
      this.addedCurrencies
        .map((c, i) => i !== index ? c.data.id : null)
        .filter(id => id !== null)
    );
    return this.currencies.filter(c => !usedIds.has(c.id) || c.id === currentCurrency?.id);
  }

  onCurrencyChange(index: number, currencyId: number): void {
    const current = this.addedCurrencies?.[index];
    if (!current || current.data.id === currencyId) {
      return;
    }
    const newCurrency = this.currencies.find(c => c.id === currencyId);
    if (!newCurrency) {
      return;
    }
    
    // Update the row's currency data
    this.addedCurrencies[index].data = newCurrency;
    this.addedCurrencies[index].controls.currency.patchValue(newCurrency.id);
    
    // Update all variant controls for this currency
    this.addedCurrencies[index].controls.variants.forEach((variantGroup, variantIndex) => {
      const retailPriceGroup = variantGroup.get('retail_price') as FormGroup;
      const costGroup = variantGroup.get('cost') as FormGroup;
      
      if (retailPriceGroup) {
        retailPriceGroup.patchValue({
          currency_id: newCurrency.id,
          name: newCurrency.name,
          symbol: newCurrency.symbol
        });
      }
      if (costGroup) {
        costGroup.patchValue({
          currency_id: newCurrency.id,
          name: newCurrency.name,
          symbol: newCurrency.symbol
        });
      }
    });
    
    this.recomputeAvailableCurrencies();
  }

  removeCurrency(index: number): void {
    const item = this.addedCurrencies?.[index];
    if (item) {
      if (item.controlName) {
        this.form.removeControl(item.controlName);
      }
      this.addedCurrencies.splice(index, 1);
      this.recomputeAvailableCurrencies();
    }
  }

  addCurrency(currency: ICurrency | undefined): void {
    if (!this.form) {
      console.error('Form is not initialized yet');
      return;
    }
    
    if (!currency && this.availableCurrencies.length > 0) {
      currency = this.availableCurrencies[0];
    }
    if (!currency) {
      return;
    }
    
    // At this point, currency is guaranteed to be defined
    const selectedCurrency = currency;
    
    const index = this.addedCurrencies.length;
    const controlName = `currency_${index}`;
    
    // Create currency control
    const currencyControl = this.fb.control(selectedCurrency.id, Validators.required);
    
    // Create variant controls for this currency
    const variantControls: FormGroup[] = [];
    this.variants.forEach((variant, variantIndex) => {
      // Find existing prices for this variant and currency
      const existedRetailPrice = variant.currency_prices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === selectedCurrency.id && currencyPrice.type === 'retail_price'
      );
      const existedCost = variant.currency_prices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === selectedCurrency.id && currencyPrice.type === 'cost'
      );

      const variantGroup = this.fb.group({
        variant_id: [variant.id],
        retail_price: this.fb.group({
          currency_id: [selectedCurrency.id, Validators.required],
          type: ['retail_price'],
          amount: [existedRetailPrice?.amount || null, Validators.min(0)],
          name: [selectedCurrency.name],
          symbol: [selectedCurrency.symbol]
        }),
        cost: this.fb.group({
          currency_id: [selectedCurrency.id, Validators.required],
          type: ['cost'],
          amount: [existedCost?.amount || null, Validators.min(0)],
          name: [selectedCurrency.name],
          symbol: [selectedCurrency.symbol]
        })
      });
      
      variantControls.push(variantGroup);
    });
    
    // Create a form group for this currency
    const currencyGroup = this.fb.group({
      currency: currencyControl,
      variants: this.fb.array(variantControls)
    });
    
    this.form.addControl(controlName, currencyGroup);
    
    // Subscribe to currency changes
    currencyControl.valueChanges.subscribe((currencyId: number | null) => {
      if (currencyId !== null) {
        this.onCurrencyChange(index, currencyId);
      }
    });

    this.addedCurrencies.push({
      data: selectedCurrency,
      controls: {
        currency: currencyControl,
        variants: variantControls
      },
      controlName: controlName
    });
    
    this.recomputeAvailableCurrencies();
  }

  save(): boolean | void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }

    // Build the result structure
    const variantsData = this.variants.map((variant, index) => {
      const variantForm = this.variantsFormArray[index];
      const variantCurrencyPrices: ICurrencyPrice[] = [];
      
      // Add currency prices from added currencies
      this.addedCurrencies.forEach(addedCurrency => {
        const variantControl = addedCurrency.controls.variants[index];
        if (variantControl) {
          const retailPrice = variantControl.get('retail_price')?.value;
          const cost = variantControl.get('cost')?.value;
          
          if (retailPrice && retailPrice.amount !== null && retailPrice.amount !== undefined) {
            variantCurrencyPrices.push(retailPrice);
          }
          if (cost && cost.amount !== null && cost.amount !== undefined) {
            variantCurrencyPrices.push(cost);
          }
        }
      });

      return {
        id: variant.id,
        cost: variantForm.get('cost')?.value,
        sales_price: variantForm.get('sales_price')?.value,
        currency_prices: variantCurrencyPrices
      };
    });

    this.dialogRef.close({
      variants: variantsData
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  preventNegativeInput(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'Subtract') {
      event.preventDefault();
    }
  }

  sanitize(event: any) {
    if (event.target.value < 0) {
      event.target.value = 0;
    }
  }
}
